import { useState, useEffect, useContext, useRef, useCallback } from "react";
import AppShell, { ThemeCtx } from "./AppShell";
import { C, API, ATTACH_BASE } from "./utils";

// ─── Source builder — multiple links with labels ──────────────────────────────
export function SourceBuilder({ links, setLinks, color }) {
  const [url,   setUrl]   = useState("");
  const [label, setLabel] = useState("");

  const add = () => {
    const u = url.trim();
    if(!u) return;
    const href = u.startsWith("http")?u:`https://${u}`;
    setLinks(l=>[...l, { id:Math.random(), href, label:label.trim()||"Source "+(links.length+1) }]);
    setUrl(""); setLabel("");
  };

  const remove = (id) => setLinks(l=>l.filter(x=>x.id!==id));

  return (
    <div>
      <SL>Source</SL>
      <div style={{display:"flex",flexDirection:"column",gap:"5px"}}>
        {links.map((lnk,i)=>(
          <div key={lnk.id} style={{display:"flex",alignItems:"center",gap:"6px",
            padding:"7px 10px",background:"#F8FAFC",borderRadius:"8px",
            border:"1px solid #E5E7EB"}}>
            <span style={{fontSize:"9px",fontWeight:"700",color:color,
              background:`${color}12`,borderRadius:"3px",padding:"1px 5px",flexShrink:0}}>
              {i+1}
            </span>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:"11px",fontWeight:"600",color:"#111827",
                overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                {lnk.label}
              </div>
              <div style={{fontSize:"9px",color:"#9CA3AF",overflow:"hidden",
                textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{lnk.href}</div>
            </div>
            <button onClick={()=>remove(lnk.id)}
              style={{background:"none",border:"none",cursor:"pointer",
                color:"#9CA3AF",fontSize:"14px",lineHeight:1,padding:0,flexShrink:0}}>×</button>
          </div>
        ))}
        {/* Add link row */}
        <div style={{display:"flex",gap:"5px",flexDirection:"column",
          padding:"10px",background:"#FAFAFA",borderRadius:"8px",
          border:"1px dashed #E5E7EB"}}>
          <input value={label} onChange={e=>setLabel(e.target.value)}
            placeholder="Label (e.g. FDA Approval)"
            style={{padding:"6px 9px",border:"1px solid #E5E7EB",borderRadius:"7px",
              fontSize:"11px",outline:"none",fontFamily:"inherit",background:"#fff"}}/>
          <div style={{display:"flex",gap:"5px"}}>
            <input value={url} onChange={e=>setUrl(e.target.value)}
              onKeyDown={e=>{if(e.key==="Enter"){e.preventDefault();add();}}}
              placeholder="https://…"
              style={{flex:1,padding:"6px 9px",border:"1px solid #E5E7EB",borderRadius:"7px",
                fontSize:"11px",outline:"none",fontFamily:"inherit",background:"#fff"}}/>
            <button onClick={add}
              style={{padding:"6px 12px",border:"none",borderRadius:"7px",background:color,
                color:"#fff",fontSize:"11px",fontWeight:"700",cursor:"pointer",flexShrink:0}}>
              + Add
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── HTML editor (contenteditable) ───────────────────────────────────────────
export function HtmlEditor({ value, onChange, placeholder }) {
  const ref     = useRef();
  const [focused, setFocused] = useState(false);
  const [activeFormats, setActiveFormats] = useState({});

  useEffect(()=>{
    if(ref.current && ref.current.innerHTML !== value)
      ref.current.innerHTML = value||"";
  },[]);

  const exec = (cmd, val=null) => {
    document.execCommand(cmd, false, val);
    ref.current.focus();
    updateActive();
  };

  const updateActive = () => {
    setActiveFormats({
      bold:      document.queryCommandState("bold"),
      italic:    document.queryCommandState("italic"),
      underline: document.queryCommandState("underline"),
      ul:        document.queryCommandState("insertUnorderedList"),
      ol:        document.queryCommandState("insertOrderedList"),
      justifyLeft:   document.queryCommandState("justifyLeft"),
      justifyCenter: document.queryCommandState("justifyCenter"),
      justifyRight:  document.queryCommandState("justifyRight"),
      justifyFull:   document.queryCommandState("justifyFull"),
    });
  };

  const Btn = ({cmd, val=null, active, title, children}) => (
    <button onMouseDown={e=>{e.preventDefault();exec(cmd,val);}}
      title={title}
      style={{padding:"3px 6px",border:`1px solid ${active?"#94A3B8":"#E5E7EB"}`,
        borderRadius:"4px",background:active?"#E2E8F0":"#fff",cursor:"pointer",
        fontSize:"11px",fontWeight:"600",color:active?"#1E293B":"#374151",
        minWidth:"26px",lineHeight:"16px",transition:"all 0.1s"}}>
      {children}
    </button>
  );

  const Sep = () => <div style={{width:"1px",height:"20px",background:"#E5E7EB",margin:"0 2px",flexShrink:0}}/>;

  return (
    <div style={{border:`1px solid ${focused?"#94A3B8":"#E5E7EB"}`,borderRadius:"8px",
      overflow:"hidden",transition:"border 0.15s"}}>
      {/* Toolbar */}
      <div style={{display:"flex",gap:"2px",padding:"5px 8px",background:"#F8FAFC",
        borderBottom:"1px solid #E5E7EB",flexWrap:"wrap",alignItems:"center",rowGap:"4px"}}>

        {/* Block format */}
        <select onMouseDown={e=>e.stopPropagation()}
          onChange={e=>{ exec("formatBlock",e.target.value); e.target.value=""; }}
          defaultValue=""
          style={{padding:"2px 4px",border:"1px solid #E5E7EB",borderRadius:"4px",
            fontSize:"11px",color:"#374151",background:"#fff",cursor:"pointer",
            outline:"none",fontFamily:"inherit",height:"24px"}}>
          <option value="" disabled>Format</option>
          <option value="p">Paragraph</option>
          <option value="h1">Heading 1</option>
          <option value="h2">Heading 2</option>
          <option value="h3">Heading 3</option>
          <option value="blockquote">Quote</option>
          <option value="pre">Code</option>
        </select>

        <Sep/>

        {/* Font size */}
        <select onMouseDown={e=>e.stopPropagation()}
          onChange={e=>{ exec("fontSize",e.target.value); e.target.value=""; }}
          defaultValue=""
          style={{padding:"2px 4px",border:"1px solid #E5E7EB",borderRadius:"4px",
            fontSize:"11px",color:"#374151",background:"#fff",cursor:"pointer",
            outline:"none",fontFamily:"inherit",height:"24px",width:"52px"}}>
          <option value="" disabled>Size</option>
          {[1,2,3,4,5,6,7].map(s=>(
            <option key={s} value={s}>{["8","10","12","14","18","24","36"][s-1]}px</option>
          ))}
        </select>

        <Sep/>

        {/* Text style */}
        <Btn cmd="bold"      active={activeFormats.bold}      title="Bold (Ctrl+B)"><b>B</b></Btn>
        <Btn cmd="italic"    active={activeFormats.italic}    title="Italic (Ctrl+I)"><i>I</i></Btn>
        <Btn cmd="underline" active={activeFormats.underline} title="Underline (Ctrl+U)"><u>U</u></Btn>
        <button onMouseDown={e=>{e.preventDefault();exec("strikeThrough");}}
          title="Strikethrough"
          style={{padding:"3px 6px",border:"1px solid #E5E7EB",borderRadius:"4px",
            background:"#fff",cursor:"pointer",fontSize:"11px",color:"#374151"}}>
          <s>S</s>
        </button>

        <Sep/>

        {/* Color */}
        <div title="Text color" style={{position:"relative",display:"inline-block"}}>
          <button style={{padding:"2px 5px",border:"1px solid #E5E7EB",borderRadius:"4px",
            background:"#fff",cursor:"pointer",fontSize:"11px",color:"#374151",
            display:"flex",flexDirection:"column",alignItems:"center",gap:"1px"}}>
            <span style={{fontWeight:"800",fontSize:"11px",lineHeight:1}}>A</span>
            <input type="color" onInput={e=>exec("foreColor",e.target.value)}
              style={{position:"absolute",opacity:0,width:"100%",height:"100%",
                top:0,left:0,cursor:"pointer"}}/>
            <div style={{height:"3px",width:"14px",background:"#EF4444",borderRadius:"1px"}}/>
          </button>
        </div>
        <div title="Highlight" style={{position:"relative",display:"inline-block"}}>
          <button style={{padding:"2px 5px",border:"1px solid #E5E7EB",borderRadius:"4px",
            background:"#fff",cursor:"pointer",fontSize:"11px",color:"#374151",
            display:"flex",flexDirection:"column",alignItems:"center",gap:"1px"}}>
            <span style={{fontWeight:"800",fontSize:"10px",lineHeight:1}}>H</span>
            <input type="color" defaultValue="#FBBF24" onInput={e=>exec("hiliteColor",e.target.value)}
              style={{position:"absolute",opacity:0,width:"100%",height:"100%",
                top:0,left:0,cursor:"pointer"}}/>
            <div style={{height:"3px",width:"14px",background:"#FBBF24",borderRadius:"1px"}}/>
          </button>
        </div>

        <Sep/>

        {/* Align */}
        <Btn cmd="justifyLeft"   active={activeFormats.justifyLeft}   title="Align left">
          <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
            <rect x="0" y="1" width="16" height="2" rx="1"/><rect x="0" y="5" width="11" height="2" rx="1"/>
            <rect x="0" y="9" width="16" height="2" rx="1"/><rect x="0" y="13" width="9" height="2" rx="1"/>
          </svg>
        </Btn>
        <Btn cmd="justifyCenter" active={activeFormats.justifyCenter} title="Center">
          <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
            <rect x="0" y="1" width="16" height="2" rx="1"/><rect x="2.5" y="5" width="11" height="2" rx="1"/>
            <rect x="0" y="9" width="16" height="2" rx="1"/><rect x="3.5" y="13" width="9" height="2" rx="1"/>
          </svg>
        </Btn>
        <Btn cmd="justifyRight"  active={activeFormats.justifyRight}  title="Align right">
          <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
            <rect x="0" y="1" width="16" height="2" rx="1"/><rect x="5" y="5" width="11" height="2" rx="1"/>
            <rect x="0" y="9" width="16" height="2" rx="1"/><rect x="7" y="13" width="9" height="2" rx="1"/>
          </svg>
        </Btn>
        <Btn cmd="justifyFull"   active={activeFormats.justifyFull}   title="Justify">
          <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
            <rect x="0" y="1" width="16" height="2" rx="1"/><rect x="0" y="5" width="16" height="2" rx="1"/>
            <rect x="0" y="9" width="16" height="2" rx="1"/><rect x="0" y="13" width="16" height="2" rx="1"/>
          </svg>
        </Btn>

        <Sep/>

        {/* Lists */}
        <Btn cmd="insertUnorderedList" active={activeFormats.ul} title="Bullet list">•≡</Btn>
        <Btn cmd="insertOrderedList"   active={activeFormats.ol} title="Numbered list">1≡</Btn>
        <button onMouseDown={e=>{e.preventDefault();exec("indent");}} title="Indent"
          style={{padding:"3px 6px",border:"1px solid #E5E7EB",borderRadius:"4px",
            background:"#fff",cursor:"pointer",fontSize:"11px",color:"#374151"}}>→</button>
        <button onMouseDown={e=>{e.preventDefault();exec("outdent");}} title="Outdent"
          style={{padding:"3px 6px",border:"1px solid #E5E7EB",borderRadius:"4px",
            background:"#fff",cursor:"pointer",fontSize:"11px",color:"#374151"}}>←</button>

        <Sep/>

        {/* Link + HR + Clear */}
        <button onMouseDown={e=>{e.preventDefault();
          const url=prompt("URL:");if(url)exec("createLink",url);}}
          title="Insert link"
          style={{padding:"3px 6px",border:"1px solid #E5E7EB",borderRadius:"4px",
            background:"#fff",cursor:"pointer",fontSize:"11px",color:"#374151"}}>
          🔗
        </button>
        <button onMouseDown={e=>{e.preventDefault();exec("insertHorizontalRule");}}
          title="Horizontal rule"
          style={{padding:"3px 6px",border:"1px solid #E5E7EB",borderRadius:"4px",
            background:"#fff",cursor:"pointer",fontSize:"11px",color:"#374151"}}>
          ―
        </button>
        <button onMouseDown={e=>{e.preventDefault();exec("removeFormat");}}
          title="Clear formatting"
          style={{padding:"3px 6px",border:"1px solid #E5E7EB",borderRadius:"4px",
            background:"#fff",cursor:"pointer",fontSize:"11px",color:"#374151"}}>
          ✕
        </button>
      </div>

      {/* Editable area */}
      <div ref={ref} contentEditable suppressContentEditableWarning
        onFocus={()=>setFocused(true)}
        onBlur={()=>{setFocused(false);}}
        onKeyUp={updateActive} onMouseUp={updateActive}
        onInput={()=>onChange(ref.current.innerHTML)}
        data-placeholder={placeholder}
        style={{minHeight:"120px",maxHeight:"320px",overflowY:"auto",padding:"10px 12px",
          fontSize:"12px",color:"#111827",lineHeight:1.7,outline:"none",fontFamily:"inherit",
          background:"#fff"}}/>
      <style>{`
        [contenteditable]:empty:before{content:attr(data-placeholder);color:#9CA3AF;pointer-events:none;}
        [contenteditable] h1{font-size:1.6em;font-weight:800;margin:8px 0 4px;}
        [contenteditable] h2{font-size:1.3em;font-weight:700;margin:6px 0 3px;}
        [contenteditable] h3{font-size:1.1em;font-weight:700;margin:5px 0 2px;}
        [contenteditable] blockquote{border-left:3px solid #E5E7EB;padding-left:10px;color:#6B7280;margin:6px 0;}
        [contenteditable] pre{background:#F1F5F9;padding:8px;border-radius:5px;font-family:monospace;font-size:11px;}
        [contenteditable] a{color:#2563EB;text-decoration:underline;}
        [contenteditable] hr{border:none;border-top:1px solid #E5E7EB;margin:8px 0;}
      `}</style>
    </div>
  );
}

// ─── Tabs (Sponsor / Brand / Indication / Tags) ────────────────────────────
export function ClassifyTabs({ bis, brands, setBrands, sponsors, setSponsors,
  indications, setIndications, hashtags, keywords, setKeywords, color, user, activeSub }) {

  const TABS = [
    {id:"tags",       label:"Tags"},
    {id:"sponsor",    label:"Company"},
    {id:"brand",      label:"Product"},
    {id:"indication", label:"Indication"},
  ].filter(t=>{
    if(t.id==="tags") return true;
    return true;
  });

  const [tab, setTab] = useState("tags");

  return (
    <div style={{border:"1px solid #E5E7EB",borderRadius:"10px",overflow:"hidden"}}>
      {/* Tab bar */}
      <div style={{display:"flex",background:"#F8FAFC",borderBottom:"1px solid #E5E7EB"}}>
        {TABS.map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id)}
            style={{flex:1,padding:"7px 4px",border:"none",background:"none",cursor:"pointer",
              fontSize:"11px",fontWeight:tab===t.id?"800":"500",
              color:tab===t.id?color:"#6B7280",
              borderBottom:`2px solid ${tab===t.id?color:"transparent"}`,
              transition:"all 0.12s"}}>
            {t.label}
            {t.id==="sponsor"    && sponsors.length>0    && <span style={{marginLeft:"3px",fontSize:"9px",fontWeight:"900",color}}>{sponsors.length}</span>}
            {t.id==="brand"      && brands.length>0      && <span style={{marginLeft:"3px",fontSize:"9px",fontWeight:"900",color}}>{brands.length}</span>}
            {t.id==="indication" && indications.length>0 && <span style={{marginLeft:"3px",fontSize:"9px",fontWeight:"900",color}}>{indications.length}</span>}
          </button>
        ))}
      </div>
      {/* Tab content */}
      <div style={{padding:"10px 12px",minHeight:"80px"}}>
        {tab==="tags"&&<TagsPanel hashtags={hashtags} keywords={keywords}
          setKeywords={setKeywords} color={color}/>}
        {tab==="sponsor"&&
          <PillSelectPrimary items={bis.COMPANY||[]} value={sponsors} onChange={setSponsors}
            labelKey="SPONSORNAME" idKey="SPONSORID" color={color}
            user={user} activeSub={activeSub} addType="company"
            onAdd={item=>setSponsors(s=>[...s,item])}/>}
        {tab==="brand"&&
          <PillSelectPrimary items={bis.BRAND||[]} value={brands} onChange={setBrands}
            labelKey="BRANDNAME" idKey="BRANDID" color={color}
            user={user} activeSub={activeSub} addType="product"
            onAdd={item=>setBrands(b=>[...b,item])}/>}
        {tab==="indication"&&
          <PillSelectPrimary items={bis.INDICATION||[]} value={indications} onChange={setIndications}
            labelKey="INDICATIONNAME" idKey="INDICATIONID" color={color}
            user={user} activeSub={activeSub} addType="indication"
            onAdd={item=>setIndications(ind=>[...ind,item])}/>}
      </div>
    </div>
  );
}

// ─── Tags panel ───────────────────────────────────────────────────────────────
function TagsPanel({ hashtags, keywords, setKeywords, color }) {
  const [custom, setCustom] = useState("");
  const selectedTags = (keywords||"").split(",").map(s=>s.trim()).filter(Boolean);

  const toggle = (tag) => {
    if(selectedTags.includes(tag))
      setKeywords(selectedTags.filter(t=>t!==tag).join(", "));
    else
      setKeywords([...selectedTags, tag].join(", "));
  };
  const addCustom = () => {
    if(!custom.trim()) return;
    const tag = custom.trim();
    if(!selectedTags.includes(tag)) setKeywords([...selectedTags,tag].join(", "));
    setCustom("");
  };
  const remove = (tag) => setKeywords(selectedTags.filter(t=>t!==tag).join(", "));

  return (
    <div>
      {/* Hashtag chips from category */}
      {hashtags.length>0&&<div>
        <div style={{fontSize:"9px",fontWeight:"700",color:"#9CA3AF",textTransform:"uppercase",
          letterSpacing:"0.5px",marginBottom:"5px"}}>Suggested tags</div>
        <div style={{display:"flex",flexWrap:"wrap",gap:"4px",marginBottom:"8px"}}>
          {hashtags.map(tag=>{
            const active=selectedTags.includes(tag);
            return (
              <span key={tag} onClick={()=>toggle(tag)}
                style={{padding:"2px 8px",borderRadius:"20px",fontSize:"10px",fontWeight:"600",
                  cursor:"pointer",transition:"all 0.1s",
                  background:active?color:`${color}12`,color:active?"#fff":color,
                  border:`1px solid ${active?color:`${color}33`}`}}>
                {tag}
              </span>
            );
          })}
        </div>
      </div>}
      {/* Custom tag input */}
      <div style={{display:"flex",gap:"5px",marginBottom:"8px"}}>
        <input value={custom} onChange={e=>setCustom(e.target.value)}
          onKeyDown={e=>{if(e.key==="Enter"){e.preventDefault();addCustom();}}}
          placeholder="#custom or plain keyword"
          style={{flex:1,padding:"5px 9px",border:"1px solid #E5E7EB",borderRadius:"7px",
            fontSize:"11px",outline:"none",fontFamily:"inherit"}}/>
        <button onClick={addCustom}
          style={{padding:"5px 10px",border:"none",borderRadius:"7px",background:color,
            color:"#fff",fontSize:"11px",fontWeight:"700",cursor:"pointer"}}>
          Add
        </button>
      </div>
      {/* Selected tags */}
      {selectedTags.length>0&&<div>
        <div style={{fontSize:"9px",fontWeight:"700",color:"#9CA3AF",textTransform:"uppercase",
          letterSpacing:"0.5px",marginBottom:"4px"}}>Selected</div>
        <div style={{display:"flex",flexWrap:"wrap",gap:"4px"}}>
          {selectedTags.map(tag=>(
            <span key={tag} style={{display:"inline-flex",alignItems:"center",gap:"3px",
              padding:"2px 8px",borderRadius:"20px",fontSize:"10px",fontWeight:"600",
              background:`${color}12`,color,border:`1px solid ${color}33`}}>
              {tag}
              <span onClick={()=>remove(tag)}
                style={{cursor:"pointer",opacity:0.6,fontSize:"10px",lineHeight:1}}>×</span>
            </span>
          ))}
        </div>
      </div>}
    </div>
  );
}

// ─── Pill select with primary enforcement (min 1 primary) ────────────────────
function PillSelectPrimary({ items, value, onChange, labelKey, idKey, color, user, activeSub, addType, onAdd }) {
  const [search,     setSearch]     = useState("");
  const [adding,     setAdding]     = useState(false);
  const [addErr,     setAddErr]     = useState("");
  const [localItems, setLocalItems] = useState(items||[]);

  // sync when bis reloads
  useEffect(()=>{ setLocalItems(items||[]); },[items]);

  const filtered = localItems.filter(i=>(i[labelKey]||"").toLowerCase().includes(search.toLowerCase()));
  const isSelected = (item) => (value||[]).some(v=>String(v[idKey])===String(item[idKey]));
  const isPrimary  = (item) => String((value||[])[0]?.[idKey])===String(item[idKey]);

  const toggle = (item) => {
    const cur = value||[];
    const exists = cur.find(v=>String(v[idKey])===String(item[idKey]));
    if(exists){ onChange(cur.filter(v=>String(v[idKey])!==String(item[idKey]))); }
    else { onChange([...cur, item]); }
  };
  const makePrimary = (item, e) => {
    e.stopPropagation();
    const cur=value||[];
    onChange([item,...cur.filter(v=>String(v[idKey])!==String(item[idKey]))]);
  };

  const handleAdd = async () => {
    if(!search.trim()||!user||!activeSub) return;
    setAdding(true); setAddErr("");
    const epMap  = { company:"add_company", product:"add_product", indication:"add_indication" };
    const fldMap = { company:"company",     product:"product",     indication:"indication" };
    const fd = new FormData();
    fd.append("userid",        user.USERID);
    fd.append("subprojectid",  activeSub.SUBPROJECTID);
    fd.append(fldMap[addType], search.trim());
    try {
      const r   = await fetch(`${API}/${epMap[addType]}`,
        {method:"POST", headers:{"Auth-Code":user.AUTH_CODE}, body:fd});
      const res = await r.json();
      if(String(res.STATUS)==="1"&&res.DATA){
        const newItem = Array.isArray(res.DATA) ? res.DATA[0] : res.DATA;
        const cur     = value||[];
        // add to local items so pill renders
        setLocalItems(prev=>[...prev, newItem]);
        // first selection = primary (position 0), else secondary
        const updated = cur.length===0 ? [newItem] : [...cur, newItem];
        onChange(updated);
        onAdd?.(newItem);
        setSearch("");
      } else setAddErr(res.MESSAGE||"Failed to add");
    } catch { setAddErr("Network error"); }
    setAdding(false);
  };

  return (
    <div>
      <input value={search} onChange={e=>{setSearch(e.target.value);setAddErr("");}}
        onKeyDown={e=>e.key==="Enter"&&!filtered.length&&search.trim()&&handleAdd()}
        placeholder="Search…" style={{width:"100%",padding:"5px 9px",border:"1px solid #E5E7EB",
          borderRadius:"7px",fontSize:"11px",marginBottom:"6px",outline:"none",
          fontFamily:"inherit",boxSizing:"border-box"}}/>
      <div style={{display:"flex",flexWrap:"wrap",gap:"4px"}}>
        {filtered.map(item=>{
          const sel=isSelected(item), pri=isPrimary(item);
          return (
            <button key={item[idKey]} onClick={()=>toggle(item)}
              style={{padding:"4px 10px",borderRadius:"20px",cursor:"pointer",outline:"none",
                border:`1.5px solid ${sel?color:"#E5E7EB"}`,background:sel?color:"#fff",
                color:sel?"#fff":"#374151",fontSize:"11px",fontWeight:sel?"700":"400",
                transition:"all 0.12s",display:"flex",alignItems:"center",gap:"3px",
                boxShadow:pri?`0 0 0 3px ${color}33`:"none"}}>
              {pri&&<span title="Primary" style={{fontSize:"9px"}}>★</span>}
              {item[labelKey]}
              {sel&&!pri&&<span onClick={e=>makePrimary(item,e)}
                title="Set as primary" style={{fontSize:"10px",opacity:0.55,marginLeft:"1px"}}>☆</span>}
            </button>
          );
        })}
        {!filtered.length&&search.trim()&&(
          <div style={{display:"flex",alignItems:"center",gap:"6px",width:"100%",flexWrap:"wrap"}}>
            <span style={{fontSize:"11px",color:"#9CA3AF"}}>No results for "{search}"</span>
            {addType&&activeSub?.ROLE===1&&<button onClick={handleAdd} disabled={adding}
              style={{padding:"3px 10px",borderRadius:"20px",border:`1.5px solid ${color}`,
                background:color,color:"#fff",fontSize:"11px",fontWeight:"700",
                cursor:adding?"not-allowed":"pointer",opacity:adding?0.7:1}}>
              {adding?"Adding…":`+ Add "${search}"`}
            </button>}
          </div>
        )}
        {!filtered.length&&!search.trim()&&<span style={{fontSize:"11px",color:"#9CA3AF"}}>No results</span>}
        {addErr&&<div style={{fontSize:"10px",color:"#EF4444",marginTop:"2px"}}>{addErr}</div>}
      </div>
      {(value||[]).length>1&&<div style={{fontSize:"9px",color:"#9CA3AF",marginTop:"4px"}}>
        ★ = primary — click ☆ on any selected to change
      </div>}
    </div>
  );
}

// ─── Simple pill select (single) ─────────────────────────────────────────────
export function PillSelect({ items, value, onChange, labelKey, idKey, color, maxShow=20 }) {
  const [search,   setSearch]   = useState("");
  const [showAll,  setShowAll]  = useState(false);
  const filtered = items.filter(i=>(i[labelKey]||"").toLowerCase().includes(search.toLowerCase()));
  const shown    = showAll ? filtered : filtered.slice(0, maxShow);
  const hasMore  = filtered.length > maxShow;

  return (
    <div>
      {items.length>8&&<input value={search} onChange={e=>{setSearch(e.target.value);setShowAll(false);}}
        placeholder="Search…" style={{width:"100%",padding:"5px 9px",border:"1px solid #E5E7EB",
          borderRadius:"7px",fontSize:"11px",marginBottom:"6px",outline:"none",
          fontFamily:"inherit",boxSizing:"border-box"}}/>}
      <div style={{display:"flex",flexWrap:"wrap",gap:"4px"}}>
        {/* Always show selected even if not in shown slice */}
        {value&&!shown.find(i=>i[idKey]===value[idKey])&&(
          <button onClick={()=>onChange(null)}
            style={{padding:"4px 10px",borderRadius:"20px",cursor:"pointer",outline:"none",
              border:`1.5px solid ${color}`,background:color,
              color:"#fff",fontSize:"11px",fontWeight:"700",transition:"all 0.12s"}}>
            {value[labelKey]}
          </button>
        )}
        {shown.map(item=>{
          const sel=value?.[idKey]===item[idKey];
          return (
            <button key={item[idKey]} onClick={()=>onChange(sel?null:item)}
              style={{padding:"4px 10px",borderRadius:"20px",cursor:"pointer",outline:"none",
                border:`1.5px solid ${sel?color:"#E5E7EB"}`,background:sel?color:"#fff",
                color:sel?"#fff":"#374151",fontSize:"11px",fontWeight:sel?"700":"400",
                transition:"all 0.12s"}}>
              {item[labelKey]}
            </button>
          );
        })}
        {!shown.length&&!value&&<span style={{fontSize:"11px",color:"#9CA3AF"}}>No results</span>}
      </div>
      {hasMore&&!showAll&&<button onClick={()=>setShowAll(true)}
        style={{marginTop:"5px",padding:"3px 10px",borderRadius:"20px",cursor:"pointer",
          border:`1px dashed ${color}44`,background:"none",
          color:"#9CA3AF",fontSize:"10px",fontWeight:"600"}}>
        +{filtered.length-maxShow} more…
      </button>}
      {showAll&&hasMore&&<button onClick={()=>setShowAll(false)}
        style={{marginTop:"5px",padding:"3px 10px",borderRadius:"20px",cursor:"pointer",
          border:`1px dashed #D1D5DB`,background:"none",
          color:"#9CA3AF",fontSize:"10px",fontWeight:"600"}}>
        Show less ↑
      </button>}
    </div>
  );
}

const PRIORITY_CFG = {
  1:{label:"Low",   color:"#6B7280",bg:"#F3F4F6",icon:"—"},
  2:{label:"Medium",color:"#D97706",bg:"#FFFBEB",icon:"🏳"},
  3:{label:"High",  color:"#DC2626",bg:"#FEF2F2",icon:"🚩"},
};

const SL = ({children,req}) => (
  <div style={{fontSize:"10px",fontWeight:"800",color:"#9CA3AF",textTransform:"uppercase",
    letterSpacing:"0.7px",marginBottom:"5px"}}>
    {children}{req&&<span style={{color:"#EF4444",marginLeft:"2px"}}>*</span>}
  </div>
);
const FI = ({value,onChange,placeholder,...rest}) => (
  <input value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder}
    style={{width:"100%",padding:"7px 10px",border:"1px solid #E5E7EB",borderRadius:"8px",
      fontSize:"12px",color:"#111827",background:"#fff",outline:"none",fontFamily:"inherit",
      boxSizing:"border-box"}} {...rest}/>
);

// ─── Right panel ──────────────────────────────────────────────────────────────
export function RightPanel({ files, setFiles, color, previewMode,
  doctype, title, notes, keywords, brands, sponsors, indications, phase, priority, link,
  session=null, editDoc=null }) {
  const inputRef = useRef();
  const add = (newFiles) => setFiles(p=>[...p,...Array.from(newFiles).map(f=>({
    file:f, id:Math.random(), previewUrl:f.type.startsWith("image/")?URL.createObjectURL(f):null
  }))]);
  const imgs  = files.filter(f=>f.previewUrl);
  const other = files.filter(f=>!f.previewUrl);
  const pc    = priority ? PRIORITY_CFG[priority] : null;

  if(previewMode) return (
    <div style={{height:"100%",display:"flex",flexDirection:"column",overflow:"hidden"}}>
      <div style={{padding:"9px 14px",background:"#fff",borderBottom:"1px solid #E5E7EB",
        fontSize:"10px",fontWeight:"700",color:"#9CA3AF",textTransform:"uppercase",
        letterSpacing:"0.6px",flexShrink:0}}>Live Preview</div>
      <div style={{flex:1,overflowY:"auto",background:"#F8FAFC"}}>
        <div style={{padding:"14px",background:`linear-gradient(135deg,${color}EE,${color}77)`,
          position:"relative",overflow:"hidden"}}>
          <div style={{position:"absolute",right:"-12px",top:"-12px",width:"70px",height:"70px",
            borderRadius:"50%",background:"rgba(255,255,255,0.07)"}}/>
          {doctype&&<div style={{display:"inline-flex",background:"rgba(255,255,255,0.2)",
            borderRadius:"20px",padding:"1px 8px",marginBottom:"4px",fontSize:"9px",
            fontWeight:"700",color:"rgba(255,255,255,0.9)",textTransform:"uppercase",
            letterSpacing:"0.6px"}}>{doctype.DOCTYPENAME}</div>}
          <div style={{fontSize:"14px",fontWeight:"800",color:"#fff",lineHeight:1.3,
            marginBottom:"4px",minHeight:"18px"}}>
            {title||<span style={{opacity:0.4}}>Title…</span>}
          </div>
          <div style={{display:"flex",flexWrap:"wrap",gap:"4px",alignItems:"center"}}>
            {sponsors[0]&&<span style={{fontSize:"10px",color:"rgba(255,255,255,0.85)",fontWeight:"600"}}>{sponsors[0].SPONSORNAME}</span>}
            {brands[0]&&<span style={{fontSize:"10px",color:"rgba(255,255,255,0.7)"}}>· {brands[0].BRANDNAME}</span>}
            {phase&&<span style={{fontSize:"9px",fontWeight:"700",padding:"1px 7px",borderRadius:"5px",background:"rgba(255,255,255,0.2)",color:"#fff"}}>{phase.PHASENAME}</span>}
            {pc&&<span style={{fontSize:"11px"}}>{pc.icon}</span>}
          </div>
          {indications[0]&&<div style={{fontSize:"9px",color:"rgba(255,255,255,0.65)",marginTop:"2px"}}>{indications[0].INDICATIONNAME}</div>}
          {keywords&&<div style={{display:"flex",flexWrap:"wrap",gap:"3px",marginTop:"5px"}}>
            {keywords.split(",").map(k=>k.trim()).filter(Boolean).map((k,i)=>(
              <span key={i} style={{fontSize:"8px",padding:"1px 6px",borderRadius:"20px",
                background:"rgba(255,255,255,0.18)",color:"rgba(255,255,255,0.9)",fontWeight:"600"}}>{k}</span>
            ))}
          </div>}
        </div>
        {imgs.length>0&&<div style={{padding:"10px 10px 0"}}>
          <img src={imgs[0].previewUrl} alt="" style={{width:"100%",borderRadius:"8px",
            maxHeight:"160px",objectFit:"contain",background:"#fff",border:"1px solid #E5E7EB"}}/>
        </div>}
        {notes&&<div style={{margin:"10px",padding:"10px",background:"#fff",borderRadius:"8px",
          border:"1px solid #E5E7EB",fontSize:"11px",color:"#374151",lineHeight:1.65,
          maxHeight:"200px",overflowY:"auto"}}
          dangerouslySetInnerHTML={{__html:notes}}/>}
        {link&&<div style={{padding:"0 10px 8px",fontSize:"10px",color:"#9CA3AF",wordBreak:"break-all"}}>🔗 {link}</div>}
        {other.length>0&&<div style={{padding:"0 10px 10px",display:"flex",flexWrap:"wrap",gap:"3px"}}>
          {other.map(f=>{const ext=f.file.name.split(".").pop().toUpperCase();
            const ec=ext==="PDF"?"#DC2626":ext==="PPTX"||ext==="PPT"?"#D97706":ext==="DOCX"?"#2563EB":"#6B7280";
            return <span key={f.id} style={{fontSize:"9px",padding:"2px 7px",borderRadius:"5px",
              background:`${ec}12`,color:ec,border:`1px solid ${ec}33`,fontWeight:"600"}}>
              📄 {f.file.name.length>18?f.file.name.slice(0,16)+"…":f.file.name}
            </span>;})}
        </div>}
        {!title&&!notes&&!doctype&&!files.length&&<div style={{textAlign:"center",
          color:"#D1D5DB",fontSize:"12px",padding:"30px 16px"}}>Fill the form to preview</div>}
      </div>
    </div>
  );

  return (
    <div style={{height:"100%",display:"flex",flexDirection:"column",overflow:"hidden"}}>
      <div style={{padding:"9px 14px",background:"#fff",borderBottom:"1px solid #E5E7EB",
        flexShrink:0,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <span style={{fontSize:"10px",fontWeight:"700",color:"#9CA3AF",textTransform:"uppercase",
          letterSpacing:"0.6px"}}>Attachments{files.length>0&&` (${files.length})`}</span>
        <button onClick={()=>inputRef.current.click()}
          style={{border:`1px solid ${color}44`,background:`${color}08`,color,
            borderRadius:"6px",padding:"2px 8px",fontSize:"10px",fontWeight:"700",cursor:"pointer"}}>
          + Add
        </button>
        <input ref={inputRef} type="file" multiple style={{display:"none"}}
          onChange={e=>add(e.target.files)}/>
      </div>
      <div style={{flex:1,overflowY:"auto",padding:"10px"}}>
        {files.length===0&&<div
          onDragOver={e=>{e.preventDefault();e.currentTarget.style.borderColor=color;}}
          onDragLeave={e=>e.currentTarget.style.borderColor="#D1D5DB"}
          onDrop={e=>{e.preventDefault();e.currentTarget.style.borderColor="#D1D5DB";add(e.dataTransfer.files);}}
          onClick={()=>inputRef.current.click()}
          style={{border:"2px dashed #D1D5DB",borderRadius:"10px",padding:"30px 16px",
            textAlign:"center",cursor:"pointer",background:"#FAFAFA",transition:"border 0.15s"}}>
          <div style={{fontSize:"22px",marginBottom:"5px"}}>⊡</div>
          <div style={{fontSize:"11px",color:"#9CA3AF"}}>Drop files or <span style={{color,fontWeight:"600"}}>browse</span></div>
        </div>}
        {imgs.length>0&&<div style={{marginBottom:"8px"}}>
          <div style={{fontSize:"9px",fontWeight:"700",color:"#9CA3AF",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:"4px"}}>Images</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"4px"}}>
            {imgs.map(f=>(
              <div key={f.id} style={{position:"relative",borderRadius:"7px",overflow:"hidden",border:"1px solid #E5E7EB"}}>
                <img src={f.previewUrl} alt="" style={{width:"100%",height:"72px",objectFit:"cover"}}/>
                <button onClick={()=>{
                    if(f.existing&&f.attachmentId){
                      const fd=new FormData();
                      fd.append("userid",session?.user?.USERID||"");
                      fd.append("documentid",editDoc?.DOCUMENTID||"");
                      fd.append("attachmentid",f.attachmentId);
                      fetch(`${API}/delete_attach`,{method:"POST",headers:{"Auth-Code":session?.user?.AUTH_CODE||""},body:fd}).catch(()=>{});
                    }
                    setFiles(fl=>fl.filter(x=>x.id!==f.id));
                  }}
                  style={{position:"absolute",top:"3px",right:"3px",width:"16px",height:"16px",borderRadius:"50%",
                    border:"none",background:"rgba(0,0,0,0.55)",color:"#fff",fontSize:"10px",cursor:"pointer",
                    lineHeight:1,padding:0,display:"flex",alignItems:"center",justifyContent:"center"}}>×</button>
                <div style={{padding:"2px 4px",fontSize:"8px",color:"#6B7280",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{f.file.name}</div>
              </div>
            ))}
          </div>
        </div>}
        {other.length>0&&<div style={{display:"flex",flexDirection:"column",gap:"3px"}}>
          {imgs.length>0&&<div style={{fontSize:"9px",fontWeight:"700",color:"#9CA3AF",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:"3px"}}>Files</div>}
          {other.map(f=>{const ext=f.file.name.split(".").pop().toUpperCase();
            const ec=ext==="PDF"?"#DC2626":ext==="PPTX"||ext==="PPT"?"#D97706":ext==="DOCX"?"#2563EB":"#6B7280";
            return (
              <div key={f.id} style={{display:"flex",alignItems:"center",gap:"6px",padding:"5px 7px",
                background:"#fff",borderRadius:"6px",border:"1px solid #E5E7EB"}}>
                <span style={{background:ec,color:"#fff",borderRadius:"3px",padding:"0 3px",fontSize:"7px",fontWeight:"800",flexShrink:0}}>{ext}</span>
                <span style={{flex:1,fontSize:"10px",color:"#374151",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{f.file.name}</span>
                <span style={{fontSize:"9px",color:"#9CA3AF",flexShrink:0}}>{(f.file.size/1024).toFixed(0)}K</span>
                <button onClick={()=>{
                    if(f.existing&&f.attachmentId){
                      const fd=new FormData();
                      fd.append("userid",session?.user?.USERID||"");
                      fd.append("documentid",editDoc?.DOCUMENTID||"");
                      fd.append("attachmentid",f.attachmentId);
                      fetch(`${API}/delete_attach`,{method:"POST",headers:{"Auth-Code":session?.user?.AUTH_CODE||""},body:fd}).catch(()=>{});
                    }
                    setFiles(fl=>fl.filter(x=>x.id!==f.id));
                  }}
                  style={{background:"none",border:"none",cursor:"pointer",color:"#9CA3AF",fontSize:"14px",lineHeight:1,padding:0}}>×</button>
              </div>
            );})}
        </div>}
        {files.length>0&&<div onDragOver={e=>{e.preventDefault();e.currentTarget.style.opacity="1";}}
          onDragLeave={e=>e.currentTarget.style.opacity="0.5"}
          onDrop={e=>{e.preventDefault();e.currentTarget.style.opacity="0.5";add(e.dataTransfer.files);}}
          style={{marginTop:"6px",border:"1.5px dashed #E5E7EB",borderRadius:"8px",padding:"8px",
            textAlign:"center",cursor:"pointer",opacity:0.5,fontSize:"10px",color:"#9CA3AF",transition:"opacity 0.15s"}}
          onClick={()=>inputRef.current.click()}>+ Drop more</div>}
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export function CaptureContent({ session, editDoc=null, onEditDone=null }) {
  const user      = session?.user;
  const ctx       = useContext(ThemeCtx)||{};
  const activeSub = ctx.activeSub || session?.subproject;
  const theme     = ctx.theme||{};
  const color     = theme.primary || C.primary;
  const isAdmin   = activeSub?.ROLE===1;

  const [bis, setBis]         = useState(null);
  const [bisLoading, setBisLoading] = useState(false);
  useEffect(()=>{
    if(!user||!activeSub) return;
    setBisLoading(true);
    fetch(`${API}/bis?userid=${user.USERID}&subprojectid=${activeSub.SUBPROJECTID}`,
      {headers:{"Auth-Code":user.AUTH_CODE}})
      .then(r=>r.json())
      .then(r=>{
        if(r.STATUS==="1"){
          setBis(r.DATA);
          const cats = r.DATA?.CATEGORY||[];
          if(!editDoc){
            const def = cats.find(c=>(c.DOCTYPENAME||"").toLowerCase().includes("general"))
                     || cats.find(c=>c.IS_DEFAULT===1) || cats[0] || null;
            if(def) setDoctype(def);
          }
        }
      })
      .catch(()=>{}).finally(()=>setBisLoading(false));
  },[activeSub?.SUBPROJECTID]);

  const [showAll,   setShowAll]   = useState(false);
  const [previewMode,setPreviewMode] = useState(false);
  const [isDraft,   setIsDraft]   = useState(true);
  const [doctype,   setDoctype]   = useState(null);
  const [title,     setTitle]     = useState("");
  const [notes,     setNotes]     = useState("");
  const [link,      setLink]      = useState("");
  const [keywords,  setKeywords]  = useState("");
  const [priority,  setPriority]  = useState(null);
  const [group,     setGroup]     = useState(null);
  const [brands,    setBrands]    = useState([]);
  const [sponsors,  setSponsors]  = useState([]);
  const [indications,setIndications] = useState([]);
  const [phase,     setPhase]     = useState(null);
  const [congress,  setCongress]  = useState(null);
  const [country,   setCountry]   = useState(null);
  const [trialStatus,setTrialStatus] = useState(null);
  const [clinicalTrial,setClinicalTrial] = useState(null);
  const [targetN,   setTargetN]   = useState("");
  const [currentN,  setCurrentN]  = useState("");
  const [acronyms,  setAcronyms]  = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate,   setEndDate]   = useState("");
  const [location,  setLocation]  = useState("");
  const [eventTime, setEventTime] = useState("");
  const [timezone,  setTimezone]  = useState("");
  const [secureLvl, setSecureLvl] = useState("0");
  const [launchDate,setLaunchDate]= useState("");
  const [lassumption,setLassumption] = useState("");
  const [links,     setLinks]     = useState([]);
  const [sourceDetail,setSourceDetail] = useState("");
  const [files,     setFiles]     = useState([]);
  const [notify,    setNotify]    = useState(false);
  const [submitting,setSubmitting]= useState(false);
  const [error,     setError]     = useState(null);
  const [toast,     setToast]     = useState(null);
  const [editorKey, setEditorKey]  = useState(0);
  const [pillKey,   setPillKey]    = useState(0);

  // Pre-populate hashtags when doctype changes
  const hashtags = doctype ? (bis?.CATEGORY?.find(c=>c.DOCTYPEID===doctype.DOCTYPEID)?.HASHTAGS||[]) : [];

  // Pre-populate group with PRIMARY_GRP
  useEffect(()=>{
    if(!bis?.GROUP?.length||editDoc) return;
    const primary = bis.GROUP.find(g=>g.PRIMARY_GRP==="YES") || bis.GROUP[0];
    if(primary) setGroup(primary);
  },[bis]);

  // Pre-fill when editDoc provided
  useEffect(()=>{
    if(!editDoc||!bis) return;
    const d=editDoc;
    const dt=(bis.CATEGORY||[]).find(c=>c.DOCTYPEID===d.DOCTYPEID)||null;
    if(dt) setDoctype(dt);
    setTitle(d.SUBJECT||d.TITLE||"");
    setNotes(d.NOTES||""); setEditorKey(k=>k+1);
    // Parse link into links array
    if(d.LINK){
      const parser=new DOMParser().parseFromString(d.LINK,"text/html");
      const anchors=Array.from(parser.querySelectorAll("a"));
      if(anchors.length>0) setLinks(anchors.map(a=>({id:Math.random(),href:a.getAttribute("href")||"",label:a.textContent.trim()||"Source"})).filter(l=>l.href));
      else { const p=parser.body.textContent.trim(); if(p) setLinks([{id:Math.random(),href:p,label:"Source"}]); }
    }
    setKeywords(d.KEYWORDS||"");
    if(d.STATEID) setIsDraft(String(d.STATEID)==="1");
    if(d.NOTIFY!==undefined) setNotify(String(d.NOTIFY)==="1");
    // Priority — try by ID, fallback by name
    setPriority((bis.PRIORITY||[]).find(p=>
      (d.PRIORITYID && p.PRIORITYID===d.PRIORITYID) ||
      (d.PRIORITYNAME && p.PRIORITYNAME===d.PRIORITYNAME)
    )||null);
    setSecureLvl(String(d.SECURELVL||d.SECLEVEL||"0"));
    setAcronyms(d.ACRONYMS||"");
    setStartDate(d.STARTDATE||"");
    setEndDate(d.ENDDATE||"");
    setLocation(d.LOCATION||d.EVENT_LOCATION||"");
    // DOCDATE prefilled for all categories
    if(d.DOCDATE) {
      const parsed = new Date(d.DOCDATE.replace(/,/g,""));
      if(!isNaN(parsed)) {
        const y=parsed.getFullYear(),m=String(parsed.getMonth()+1).padStart(2,"0"),dd=String(parsed.getDate()).padStart(2,"0");
        setEventTime(`${y}-${m}-${dd}`);
      }
    }
    // EVENTTIME extra for events (datetime-local)
    if(d.EVENTTIME) setEventTime(d.EVENTTIME);
    setLaunchDate(d.LAUNCHDATE||"");
    setLassumption(d.LAUNCHASSUMPTIONS||d.LASSUMPTION||"");
    // Prefill timezone by ZONEID
    if(d.ZONEID&&bis?.TIMEZONE?.length){
      const tz=bis.TIMEZONE.find(x=>String(x.ZONEID)===String(d.ZONEID));
      if(tz) setTimezone(tz);
    }
    setTargetN(d.TARGETN!=null?String(d.TARGETN):"");
    setCurrentN(d.CURRENTN!=null?String(d.CURRENTN):"");
    const remap=(arr,bisArr,idKey)=>arr.filter(i=>i&&String(i[idKey])!=="0")
      .map(i=>{const f=(bisArr||[]).find(b=>b[idKey]===i[idKey]);return f?{...f,PRIMARY:i.PRIMARY}:i;})
      .filter(Boolean).sort((a,b)=>(b.PRIMARY||0)-(a.PRIMARY||0));
    setSponsors(remap(Array.isArray(d.COMPANY)?d.COMPANY:[],bis.COMPANY,"SPONSORID"));
    setBrands(remap(Array.isArray(d.PRODUCTS)?d.PRODUCTS:[],bis.BRAND,"BRANDID"));
    setIndications(remap(Array.isArray(d.INDICATIONS)?d.INDICATIONS:[],bis.INDICATION,"INDICATIONID"));
    // Phase — try by ID, fallback by name
    if(d.PHASEID||d.PHASENAME) setPhase((bis.PHASE||[]).find(p=>
      (d.PHASEID&&p.PHASEID===d.PHASEID)||(d.PHASENAME&&p.PHASENAME===d.PHASENAME)
    )||null);
    // Congress — try by ID, fallback by name
    if(d.CONGRESSID||d.CONGRESSNAME) setCongress((bis.CONGRESS||[]).find(c=>
      (d.CONGRESSID&&c.CONGRESSID===d.CONGRESSID)||(d.CONGRESSNAME&&c.CONGRESSNAME===d.CONGRESSNAME)
    )||null);
    // Country — try by ID, fallback by name
    if(d.COUNTRYID||d.COUNTRYNAME) setCountry((bis.COUNTRY||[]).find(c=>
      (d.COUNTRYID&&c.COUNTRYID===d.COUNTRYID)||(d.COUNTRYNAME&&c.COUNTRYNAME===d.COUNTRYNAME)
    )||null);
    // Trial status — try by ID, fallback by name
    if(d.TRIALSTATUSID||d.TRIALSTATUSNAME) setTrialStatus((bis.TRIALSTATUS||[]).find(t=>
      (d.TRIALSTATUSID&&t.TRIALSTATUSID===d.TRIALSTATUSID)||(d.TRIALSTATUSNAME&&t.TRIALSTATUSNAME===d.TRIALSTATUSNAME)
    )||null);
    // Group — try by ID, fallback by name
    if(d.GROUPID||d.GROUPNAME) setGroup((bis.GROUP||[]).find(g=>
      (d.GROUPID&&g.GROUPID===d.GROUPID)||(d.GROUPNAME&&g.GROUPNAME===d.GROUPNAME)
    )||null);
    // Clinical trial / acronym
    if(d.ACRONYMSID) setClinicalTrial((bis["CLINICAL TRIALS"]||[]).find(t=>t.ACRONYMSID===d.ACRONYMSID)||null);
    else if(d.ACRONYMS) setClinicalTrial((bis["CLINICAL TRIALS"]||[]).find(t=>t.ACRONYMS===d.ACRONYMS)||null);
    // Pre-load existing attachments
    const chatbot = Array.isArray(d.CHATBOT)?d.CHATBOT:[];
    setFiles(chatbot.map(f=>({
      file:{ name:f.FILEPATH.split("/").pop(), size:0,
        type:/\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(f.FILEPATH)?"image/jpeg":"application/octet-stream" },
      id:          Math.random(),
      previewUrl:  /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(f.FILEPATH) ? ATTACH_BASE+f.FILEPATH : null,
      existing:    true,
      attachmentId: f.ATTACHMENTID||f.attachmentid||f.ATTACH_ID||null,
    })));
  },[bis,editDoc]);

  const defCats   = (bis?.CATEGORY||[]).filter(c=>c.IS_DEFAULT===1);
  const otherCats = (bis?.CATEGORY||[]).filter(c=>c.IS_DEFAULT!==1);
  const shownCats = showAll ? (bis?.CATEGORY||[]) : defCats;

  const reset = () => {
    setDoctype(null);setTitle("");setNotes("");setLink("");setKeywords("");
    setPriority(null);setBrands([]);setSponsors([]);setIndications([]);
    setPhase(null);setCongress(null);setCountry(null);setTrialStatus(null);
    setClinicalTrial(null);setTargetN("");setCurrentN("");setAcronyms("");
    setStartDate("");setEndDate("");setTimezone("");setLocation("");setEventTime("");
    setSecureLvl("0");setLaunchDate("");setLassumption("");setFiles([]);
    setNotify(false);setError(null);setPreviewMode(false);setIsDraft(true);
    setEditorKey(k=>k+1); setPillKey(k=>k+1);
    // Re-set group to primary
    if(bis?.GROUP?.length){
      const primary=bis.GROUP.find(g=>g.PRIMARY_GRP==="YES")||bis.GROUP[0];
      if(primary) setGroup(primary);
    }
  };

  const submit = async () => {
    if(!doctype||!title.trim()) return;
    const stateid = isAdmin ? (isDraft?1:2) : 1;
    setSubmitting(true); setError(null);
    try {
      const fmtDocDate = (d) => {
        if(!d) return "";
        const dt = new Date(d);
        if(isNaN(dt)) return d;
        const MON=["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
        const day=String(dt.getDate()).padStart(2,"0");
        const mon=MON[dt.getMonth()];
        const yr=String(dt.getFullYear()).slice(-2);
        return `${day}-${mon}-${yr}`;
      };
      const fd = new FormData();
      fd.append("userid",       user.USERID);
      fd.append("subprojectid", activeSub.SUBPROJECTID);
      fd.append("doctypeid",    doctype.DOCTYPEID);
      fd.append("title", title.trim());
      fd.append("stateid",      stateid);
      if(notes)        fd.append("notes",        notes);
      const urllink = links.length>0
        ? links.map(l=>`<a href="${l.href}">${l.label}</a>`).join(", ")
        : link;
      if(urllink)      fd.append("link",          urllink);
      if(keywords)     fd.append("keywords",      keywords);
      if(group)        fd.append("groupid",       group.GROUPID);
      if(phase)        fd.append("phaseid",       phase.PHASEID);
      if(congress)     fd.append("congressid",    congress.CONGRESSID);
      if(country)      fd.append("countryid",     country.COUNTRYID);
      if(trialStatus)  fd.append("trialstatusid", trialStatus.TRIALSTATUSID);
      if(clinicalTrial)fd.append("acronymsid",    clinicalTrial.ACRONYMSID);
      if(targetN)      fd.append("targetn",       targetN);
      if(currentN)     fd.append("currentn",      currentN);
      if(acronyms)     fd.append("acronyms",      acronyms);
      if(startDate)    fd.append("startdate",     fmtDocDate(startDate));
      if(endDate)      fd.append("enddate",       fmtDocDate(endDate));
      if(location)     fd.append("location",      location);
      if(eventTime){
        const isEvt=(doctype?.DOCTYPEABBREV||"").toUpperCase()==="EV"||(doctype?.DOCTYPENAME||"").toLowerCase().includes("event");
        const isNews=(doctype?.DOCTYPEABBREV||"").toUpperCase()==="NW"||(doctype?.DOCTYPENAME||"").toLowerCase()==="news";
        if(isEvt){
          const timePart = eventTime.includes("T") ? eventTime.split("T")[1] : "";
          const datePart = eventTime.includes("T") ? eventTime.split("T")[0] : eventTime;
          if(timePart){
            const [h,m] = timePart.split(":");
            const hr=parseInt(h), ampm=hr>=12?"PM":"AM", h12=hr%12||12;
            fd.append("eventtime", `${String(h12).padStart(2,"0")}:${m} ${ampm}`);
          }
          if(datePart) fd.append("date", fmtDocDate(datePart));
        } else if(isNews){
          fd.append("date", fmtDocDate(eventTime));
        }
      }
      if(timezone?.ZONEID) fd.append("zoneid", timezone.ZONEID);
      if(launchDate)   fd.append("launchdate",    fmtDocDate(launchDate));
      if(lassumption)  fd.append("lassumption",   lassumption);
      if(sourceDetail) fd.append("tidentification", sourceDetail);
      fd.append("priorityid", parseInt(priority)||0);
      fd.append("securelvl",  parseInt(secureLvl)||0);
      brands.forEach((b,i)=>fd.append("brandid",JSON.stringify({brandid:b.BRANDID,primary:i===0?1:0})));
      sponsors.forEach((s,i)=>fd.append("sponsorid",JSON.stringify({sponsorid:s.SPONSORID,primary:i===0?1:0})));
      indications.forEach((n,i)=>fd.append("indicationid",JSON.stringify({indicationid:n.INDICATIONID,primary:i===0?1:0})));
      const newFiles = files.filter(f=>!f.existing);
      fd.append("attachment", newFiles.length>0?"true":"false");
      fd.append("notify",     newFiles.length>0?"0":(notify?"1":"0"));

      if(editDoc) fd.append("documentid", editDoc.DOCUMENTID);
      const endpoint = editDoc ? "capture_edit" : "capture";

      // fetch with auto-retry — handles temporary server blips returning HTML instead of JSON
      const fetchWithRetry = async (url, opts, label="", retries=3) => {
        for(let attempt=1; attempt<=retries; attempt++) {
          const res = await fetch(url, opts);
          const text = await res.text();
          try {
            return JSON.parse(text);
          } catch {
            console.error(`[PharmaVision] ${label} attempt ${attempt} — server returned non-JSON (HTTP ${res.status}):`, text.slice(0,200));
            if(attempt < retries) {
              await new Promise(r=>setTimeout(r, 1500 * attempt)); // wait 1.5s, 3s
              continue;
            }
            throw new Error("We're having trouble reaching the server. Please check your connection and try again.");
          }
        }
      };

      const r  = await fetch(`${API}/${endpoint}`,{method:"POST",headers:{"Auth-Code":user.AUTH_CODE},body:fd});
      const res = await (async()=>{
        const text = await r.text();
        try { return JSON.parse(text); }
        catch {
          console.error(`[PharmaVision] ${endpoint} — non-JSON (HTTP ${r.status}):`, text.slice(0,200));
          // retry the whole capture
          for(let attempt=1; attempt<=2; attempt++){
            await new Promise(x=>setTimeout(x, 1500*attempt));
            const r2 = await fetch(`${API}/${endpoint}`,{method:"POST",headers:{"Auth-Code":user.AUTH_CODE},body:fd});
            const t2 = await r2.text();
            try { return JSON.parse(t2); } catch { continue; }
          }
          throw new Error("We're having trouble reaching the server. Please check your connection and try again.");
        }
      })();
      if(res.STATUS!=="1") throw new Error(res.MESSAGE||"Capture failed");
      const docId = res.DATA[0].DOCUMENTID;

      const filesToUpload = editDoc ? files.filter(f=>!f.existing) : files;
      for(let i=0;i<filesToUpload.length;i++){
        const isLast=i===filesToUpload.length-1;
        const ufd=new FormData();
        ufd.append("userid",     user.USERID);
        ufd.append("documentid", docId);
        ufd.append("attachment", isLast?"false":"true");
        ufd.append("notify",     isLast?(notify?"1":"0"):"0");
        ufd.append("files",      filesToUpload[i].file);
        const ures = await fetch(`${API}/upload_thumbs`,{method:"POST",headers:{"Auth-Code":user.AUTH_CODE},body:ufd});
        const utext = await ures.text();
        try { JSON.parse(utext); }
        catch {
          console.error(`[PharmaVision] upload_thumbs — non-JSON (HTTP ${ures.status}):`, utext.slice(0,200));
          // retry once
          await new Promise(x=>setTimeout(x,2000));
          await fetch(`${API}/upload_thumbs`,{method:"POST",headers:{"Auth-Code":user.AUTH_CODE},body:ufd});
        }
      }
      // Keep selected type, clear all other fields
      setTitle(""); setNotes(""); setEditorKey(k=>k+1); setPillKey(k=>k+1); setLink(""); setKeywords("");
      setPriority(null); setBrands([]); setSponsors([]); setIndications([]);
      setPhase(null); setCongress(null); setCountry(null); setTrialStatus(null);
      setClinicalTrial(null); setTargetN(""); setCurrentN(""); setAcronyms("");
      setStartDate(""); setEndDate(""); setLocation(""); setEventTime("");
      setTimezone(""); setLaunchDate(""); setLassumption(""); setLinks([]);
      setSourceDetail(""); setFiles([]); setError(null); setGroup(g=>{
        // Re-apply PRIMARY_GRP default
        if(bis?.GROUP?.length){
          const primary=bis.GROUP.find(x=>x.PRIMARY_GRP==="YES")||bis.GROUP[0];
          return primary||g;
        }
        return g;
      });
      // Show toast top-right
      setToast({ msg: res.MESSAGE||"Saved successfully!", type:"success" });
      if(onEditDone) { setTimeout(()=>onEditDone(), 1500); return; }
      setTimeout(()=>setToast(null), 4000);
    } catch(e){ setError(e.message||"Something went wrong"); }
    setSubmitting(false);
  };



  return (
    <div style={{display:"flex",height:"100%",overflow:"hidden",position:"relative"}}>
      {/* Toast notification */}
      {toast&&(
        <div style={{position:"fixed",top:"60px",right:"20px",zIndex:9999,
          padding:"12px 16px",borderRadius:"10px",
          background:toast.type==="success"?color:"#DC2626",
          color:"#fff",fontSize:"12px",fontWeight:"600",
          boxShadow:"0 4px 20px rgba(0,0,0,0.2)",
          display:"flex",alignItems:"center",gap:"10px",
          minWidth:"220px",maxWidth:"340px",
          animation:"_toast 0.25s ease"}}>
          <style>{"@keyframes _toast{from{opacity:0;transform:translateY(-8px)}to{opacity:1;transform:translateY(0)}}"}</style>
          <span style={{fontSize:"16px"}}>{toast.type==="success"?"✓":"✕"}</span>
          <span style={{flex:1,lineHeight:1.4}}>{toast.msg}</span>
          <button onClick={()=>setToast(null)}
            style={{background:"rgba(255,255,255,0.25)",border:"none",color:"#fff",
              borderRadius:"5px",padding:"2px 8px",cursor:"pointer",fontSize:"13px",
              lineHeight:1,flexShrink:0}}>×</button>
        </div>
      )}

      {/* ── LEFT: Form ── */}
      <div style={{flex:1,overflowY:"auto",padding:"18px 22px",minWidth:0,
        display:"flex",flexDirection:"column",gap:"13px"}}>

        <div>
          <div style={{fontSize:"17px",fontWeight:"900",color:"#111827",letterSpacing:"-0.4px"}}>{editDoc?"Edit":"Capture"}</div>
          <div style={{fontSize:"11px",color:"#9CA3AF",marginTop:"1px"}}>{activeSub?.SUMMARY}</div>
        </div>

        {/* Type */}
        <div>
          <SL req>Type</SL>
          {bisLoading&&<div style={{fontSize:"11px",color:"#9CA3AF"}}>Loading…</div>}
          <div style={{display:"flex",flexWrap:"wrap",gap:"5px"}}>
            {(bis?.CATEGORY||[]).map(cat=>{
              const sel=doctype?.DOCTYPEID===cat.DOCTYPEID;
              return (
                <button key={cat.DOCTYPEID} onClick={()=>setDoctype(sel?null:cat)}
                  style={{padding:"5px 12px",borderRadius:"8px",cursor:"pointer",outline:"none",
                    border:`1.5px solid ${sel?color:"#E5E7EB"}`,background:sel?color:"#FAFAFA",
                    color:sel?"#fff":"#374151",fontSize:"11px",fontWeight:sel?"700":"400",
                    transition:"all 0.12s"}}>
                  {cat.DOCTYPENAME}
                </button>
              );
            })}
          </div>
        </div>

        {/* Group + Priority + Security in one row */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:"8px"}}>
          <div>
            <SL>Group</SL>
            {bis?.GROUP?.length>0
              ? <select value={group?.GROUPID||""} onChange={e=>{
                    const g=bis.GROUP.find(x=>String(x.GROUPID)===e.target.value)||null;
                    setGroup(g);
                  }}
                  style={{width:"100%",padding:"6px 8px",border:"1px solid #E5E7EB",
                    borderRadius:"8px",fontSize:"11px",color:"#374151",background:"#fff",
                    outline:"none",fontFamily:"inherit",cursor:"pointer"}}>
                  <option value="">None</option>
                  {bis.GROUP.map(g=>(
                    <option key={g.GROUPID} value={g.GROUPID}>{g.GROUPNAME}</option>
                  ))}
                </select>
              : <select style={{width:"100%",padding:"6px 8px",border:"1px solid #E5E7EB",
                    borderRadius:"8px",fontSize:"11px",color:"#9CA3AF",background:"#fff",
                    outline:"none",fontFamily:"inherit"}} disabled>
                  <option>—</option>
                </select>}
          </div>
          <div>
            <SL>Priority</SL>
            <select value={priority||""} onChange={e=>setPriority(e.target.value?Number(e.target.value):null)}
              style={{width:"100%",padding:"6px 8px",border:"1px solid #E5E7EB",
                borderRadius:"8px",fontSize:"11px",color:"#374151",background:"#fff",
                outline:"none",fontFamily:"inherit",cursor:"pointer"}}>
              <option value="">None</option>
              {(bis?.PRIORITY||[
                {PRIORITYID:1,PRIORITYNAME:"Low"},
                {PRIORITYID:2,PRIORITYNAME:"Medium"},
                {PRIORITYID:3,PRIORITYNAME:"High"}
              ]).map(p=>(
                <option key={p.PRIORITYID} value={p.PRIORITYID}>{p.PRIORITYNAME}</option>
              ))}
            </select>
          </div>
          <div>
            <SL>Security</SL>
            <select value={secureLvl} onChange={e=>setSecureLvl(e.target.value)}
              style={{width:"100%",padding:"6px 8px",border:"1px solid #E5E7EB",
                borderRadius:"8px",fontSize:"11px",color:"#374151",background:"#fff",
                outline:"none",fontFamily:"inherit",cursor:"pointer"}}>
              <option value="0">🌐 Open</option>
              <option value="1">🔒 Low</option>
              <option value="2">🔐 Medium</option>
              <option value="3">🛡 High</option>
            </select>
          </div>
        </div>

        {/* Title */}
        <div>
          <SL req>Subject</SL>
          <FI value={title} onChange={setTitle} placeholder="What is this about…"/>
        </div>

        {/* Notes — HTML editor */}
        <div>
          <SL>Notes</SL>
          <HtmlEditor key={editorKey} value={notes} onChange={setNotes}
            placeholder="Key findings, summary, body content…"/>
        </div>



        {/* Classify tabs: Brand / Sponsor / Indication / Tags */}
        <div>
          <SL>Classify</SL>
          <ClassifyTabs bis={bis} brands={brands} setBrands={setBrands}
            sponsors={sponsors} setSponsors={setSponsors}
            indications={indications} setIndications={setIndications}
            hashtags={hashtags} keywords={keywords} setKeywords={setKeywords}
            color={color} user={user} activeSub={activeSub}/>
        </div>

        {/* Phase */}
        {bis?.PHASE?.length>0&&<div>
          <SL>Phase</SL>
          <PillSelect key={`ps_1_${pillKey}`} items={bis.PHASE} value={phase} onChange={setPhase}
            labelKey="PHASENAME" idKey="PHASEID" color={color}/>
        </div>}


        {/* Clinical Trial picker — hide for CT type */}
        {bis?.["CLINICAL TRIALS"]?.length>0&&!(
          (doctype?.DOCTYPEABBREV||"").toUpperCase()==="CT"||
          (doctype?.DOCTYPENAME||"").toLowerCase().includes("clinical")
        )&&<div>
          <SL>Clinical Trial</SL>
          <PillSelect key={`ps_2_${pillKey}`} items={bis["CLINICAL TRIALS"]} value={clinicalTrial}
            onChange={setClinicalTrial} labelKey="ACRONYMS" idKey="ACRONYMSID" color={color} maxShow={8}/>
        </div>}


        {/* Congress — hide for CT type */}
        {bis?.CONGRESS?.length>0&&!(
          (doctype?.DOCTYPEABBREV||"").toUpperCase()==="CT"||
          (doctype?.DOCTYPENAME||"").toLowerCase().includes("clinical")
        )&&<div>
          <SL>Congress</SL>
          <PillSelect key={`ps_3_${pillKey}`} items={bis.CONGRESS} value={congress} onChange={setCongress}
            labelKey="CONGRESSNAME" idKey="CONGRESSID" color={color} maxShow={5}/>
        </div>}

        {/* Clinical Trial specific fields */}
        {(()=>{
          const isCT = (doctype?.DOCTYPEABBREV||"").toUpperCase()==="CT" ||
            (doctype?.DOCTYPENAME||"").toLowerCase().includes("clinical");
          return isCT ? (
            <>
              {/* Trial name + Source ID + Start + End dates */}
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"8px"}}>
                <div><SL>Trial</SL><FI value={acronyms} onChange={setAcronyms} placeholder="e.g. PRISM-1"/></div>
                <div><SL>Source Identification</SL><FI value={sourceDetail} onChange={setSourceDetail} placeholder="NCTID"/></div>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"8px"}}>
                <div><SL>Start Date</SL><SmartDateInput value={startDate} onChange={setStartDate}/></div>
                <div><SL>End Date</SL><SmartDateInput value={endDate} onChange={setEndDate}/></div>
              </div>
              {/* Target Count + Actual Count + Trial Status */}
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:"8px"}}>
                <div><SL>Target Count</SL><FI value={targetN} onChange={setTargetN} placeholder="610" type="number"/></div>
                <div><SL>Actual Count</SL><FI value={currentN} onChange={setCurrentN} placeholder="245" type="number"/></div>
                {bis?.TRIALSTATUS?.length>0&&<div>
                  <SL>Trial Status</SL>
                  <PillSelect key={`ps_4_${pillKey}`} items={bis.TRIALSTATUS} value={trialStatus} onChange={setTrialStatus}
                    labelKey="TRIALSTATUSNAME" idKey="TRIALSTATUSID" color={color}/>
                </div>}
              </div>
              {/* Country */}
              {bis?.COUNTRY?.length>0&&<div>
                <SL>Country</SL>
                <PillSelect key={`ps_5_${pillKey}`} items={bis.COUNTRY} value={country} onChange={setCountry}
                  labelKey="COUNTRYNAME" idKey="COUNTRYID" color={color}/>
              </div>}
            </>
          ) : (
            <>
              {/* Non-CT: Launch Date, Event Date & Time, Timezone, Location, Launch Assumptions */}
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:"8px"}}>
                {((doctype?.DOCTYPEABBREV||"").toUpperCase()==="PD"||(doctype?.DOCTYPENAME||"").toLowerCase().includes("product detail"))&&
                <div><SL>Launch Date</SL><SmartDateInput value={launchDate} onChange={setLaunchDate}/></div>}
                {(()=>{
                const isEv=(doctype?.DOCTYPEABBREV||"").toUpperCase()==="EV"||(doctype?.DOCTYPENAME||"").toLowerCase().includes("event");
                const isNews=(doctype?.DOCTYPEABBREV||"").toUpperCase()==="NEWS"||(doctype?.DOCTYPENAME||"").toLowerCase()==="news";
                if(!isEv&&!isNews) return null;
                return (
                  <div>
                    <SL>Date{isEv?" &amp; Time":""}</SL>
                    {isEv
                      ? <FI value={eventTime} onChange={setEventTime} type="datetime-local"/>
                      : <SmartDateInput value={eventTime} onChange={setEventTime}/>
                    }
                  </div>
                );
              })()}
                {bis?.TIMEZONE?.length>0&&(
                  (doctype?.DOCTYPEABBREV||"").toUpperCase()==="EV"||(doctype?.DOCTYPENAME||"").toLowerCase().includes("event")
                )&&<div>
                  <SL>Timezone</SL>
                  <select value={timezone?.ZONEID||""} onChange={e=>{
                      const tz=bis.TIMEZONE.find(x=>String(x.ZONEID)===e.target.value)||null;
                      setTimezone(tz||e.target.value);
                    }}
                    style={{width:"100%",padding:"6px 8px",border:"1px solid #E5E7EB",
                      borderRadius:"8px",fontSize:"11px",color:"#374151",background:"#fff",
                      outline:"none",fontFamily:"inherit",cursor:"pointer"}}>
                    <option value="">— Timezone —</option>
                    {bis.TIMEZONE.map(tz=>(
                      <option key={tz.ZONEID} value={tz.ZONEID}>
                        {tz.LOCATION}
                      </option>
                    ))}
                  </select>
                </div>}
              </div>
              {((doctype?.DOCTYPEABBREV||"").toUpperCase()==="EV"||(doctype?.DOCTYPENAME||"").toLowerCase().includes("event")||
          (doctype?.DOCTYPEABBREV||"").toUpperCase()==="NEWS"||(doctype?.DOCTYPENAME||"").toLowerCase()==="news")&&
          <div><SL>Location</SL><FI value={location} onChange={setLocation} placeholder="City, Venue"/></div>}
              {/* Start + End Date for NewsFlash */}
              {((doctype?.DOCTYPEABBREV||"").toUpperCase()==="NF"||(doctype?.DOCTYPENAME||"").toLowerCase().includes("newsflash")||(doctype?.DOCTYPENAME||"").toLowerCase().includes("news flash"))&&
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"10px"}}>
                <div><SL>Start Date</SL><SmartDateInput value={startDate} onChange={setStartDate}/></div>
                <div><SL>End Date</SL><SmartDateInput value={endDate} onChange={setEndDate}/></div>
              </div>}
              {((doctype?.DOCTYPEABBREV||"").toUpperCase()==="PD"||(doctype?.DOCTYPENAME||"").toLowerCase().includes("product detail"))&&<div>
                <SL>Launch Assumptions</SL>
                <textarea value={lassumption} onChange={e=>setLassumption(e.target.value)}
                  placeholder="e.g. Assuming priority review…" rows={2}
                  style={{width:"100%",padding:"7px 10px",border:"1px solid #E5E7EB",borderRadius:"8px",
                    fontSize:"12px",color:"#111827",background:"#fff",outline:"none",
                    fontFamily:"inherit",resize:"vertical",boxSizing:"border-box"}}/>
              </div>}
              {/* Country — for Product Details */}
              {((doctype?.DOCTYPEABBREV||"").toUpperCase()==="PD"||(doctype?.DOCTYPENAME||"").toLowerCase().includes("product detail"))&&
                bis?.COUNTRY?.length>0&&<div>
                <SL>Country</SL>
                <PillSelect key={`ps_6_${pillKey}`} items={bis.COUNTRY} value={country} onChange={setCountry}
                  labelKey="COUNTRYNAME" idKey="COUNTRYID" color={color}/>
              </div>}
            </>
          );
        })()}

        {/* Source — multi-link with labels */}
        <SourceBuilder links={links} setLinks={setLinks} color={color}/>

        {/* Notify — admin only */}
        {isAdmin&&<div>
          <SL>Notification</SL>
          <div style={{display:"flex",gap:"6px"}}>
            {[{v:true,icon:"🔔",label:"Notify"},{v:false,icon:"🔕",label:"Silent"}].map(opt=>(
              <button key={String(opt.v)} onClick={()=>setNotify(opt.v)}
                style={{flex:1,padding:"7px",borderRadius:"8px",cursor:"pointer",outline:"none",
                  border:`1.5px solid ${notify===opt.v?color:"#E5E7EB"}`,
                  background:notify===opt.v?`${color}08`:"#FAFAFA",
                  display:"flex",alignItems:"center",justifyContent:"center",gap:"5px"}}>
                <span style={{fontSize:"13px"}}>{opt.icon}</span>
                <span style={{fontSize:"10px",fontWeight:notify===opt.v?"700":"400",
                  color:notify===opt.v?color:"#374151"}}>{opt.label}</span>
              </button>
            ))}
          </div>
        </div>}

        {error&&<div style={{padding:"9px 12px",background:"#FEF2F2",borderRadius:"8px",
          fontSize:"12px",color:"#DC2626",border:"1px solid #FCA5A5"}}>{error}</div>}

        {/* Submit row */}
        <div style={{display:"flex",alignItems:"center",gap:"8px",paddingBottom:"24px"}}>
          {isAdmin&&<>
            {/* Publish / Draft toggle */}
            <div style={{display:"flex",alignItems:"center",gap:"2px",
              background:"#F1F5F9",borderRadius:"8px",padding:"3px",flexShrink:0}}>
              <button onClick={()=>setIsDraft(false)}
                style={{padding:"5px 10px",borderRadius:"5px",border:"none",cursor:"pointer",
                  fontSize:"10px",fontWeight:"700",transition:"all 0.12s",
                  background:!isDraft?color:"transparent",color:!isDraft?"#fff":"#6B7280"}}>
                🚀 Publish
              </button>
              <button onClick={()=>setIsDraft(true)}
                style={{padding:"5px 10px",borderRadius:"5px",border:"none",cursor:"pointer",
                  fontSize:"10px",fontWeight:"700",transition:"all 0.12s",
                  background:isDraft?"#64748B":"transparent",color:isDraft?"#fff":"#6B7280"}}>
                📋 Draft
              </button>
            </div>
            {/* Submit button for admin */}
            <button onClick={submit} disabled={submitting||!title.trim()||!doctype}
              style={{flex:1,padding:"10px",borderRadius:"10px",border:"none",background:color,
                color:"#fff",fontSize:"12px",fontWeight:"700",cursor:"pointer",
                opacity:submitting||!title.trim()||!doctype?0.4:1,
                display:"flex",alignItems:"center",justifyContent:"center",gap:"6px"}}>
              {submitting&&<div style={{width:"12px",height:"12px",border:"2px solid rgba(255,255,255,0.4)",
                borderTopColor:"#fff",borderRadius:"50%",animation:"_sp 0.8s linear infinite"}}/>}
              <style>{"@keyframes _sp{to{transform:rotate(360deg)}}"}</style>
              {submitting?"Saving…":isDraft?"Save Draft":"Publish"}
            </button>
          </>}
          {!isAdmin&&
            /* Non-admin: single Upload button */
            <button onClick={submit} disabled={submitting||!title.trim()||!doctype}
              style={{flex:1,padding:"10px",borderRadius:"10px",border:"none",background:color,
                color:"#fff",fontSize:"12px",fontWeight:"700",cursor:"pointer",
                opacity:submitting||!title.trim()||!doctype?0.4:1,
                display:"flex",alignItems:"center",justifyContent:"center",gap:"6px"}}>
              {submitting&&<div style={{width:"12px",height:"12px",border:"2px solid rgba(255,255,255,0.4)",
                borderTopColor:"#fff",borderRadius:"50%",animation:"_sp 0.8s linear infinite"}}/>}
              {submitting?"Uploading…":"⬆ Upload"}
            </button>
          }
        </div>
      </div>

      {/* ── RIGHT: Attachments / Preview ── */}
      <div style={{width:"290px",flexShrink:0,overflow:"hidden",
        borderLeft:"1px solid #E5E7EB",display:"flex",flexDirection:"column"}}>
        <RightPanel files={files} setFiles={setFiles} color={color}
          previewMode={previewMode}
          doctype={doctype} title={title} notes={notes} keywords={keywords}
          brands={brands} sponsors={sponsors} indications={indications}
          phase={phase} priority={priority}
          link={links.length>0?links[0].href:link}
          session={session} editDoc={editDoc}/>
      </div>
    </div>
  );
}

// ── Smart date input — single box, date picker + paste any format ──────────
function SmartDateInput({ value, onChange }) {
  const [raw, setRaw] = useState("");
  const [err, setErr] = useState("");
  const pickerRef = useRef(null);

  const toInternal = (dt) => {
    if(!dt) return "";
    const d = dt instanceof Date ? dt : new Date(dt);
    if(isNaN(d)) return "";
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
  };

  const toDisplay = (iso) => {
    if(!iso) return "";
    // if already in YYYY-MM-DD format
    if(/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
      const [y,m,d]=iso.split("-");
      return `${d}/${m}/${y}`;
    }
    // fallback - parse as date
    const dt=new Date(iso);
    if(!isNaN(dt)){
      const d=String(dt.getDate()).padStart(2,"0");
      const m=String(dt.getMonth()+1).padStart(2,"0");
      const y=dt.getFullYear();
      return `${d}/${m}/${y}`;
    }
    return iso;
  };

  const ML=["january","february","march","april","may","june","july","august","september","october","november","december"];
  const MS=["jan","feb","mar","apr","may","jun","jul","aug","sep","oct","nov","dec"];

  const parse = (s) => {
    if(!s) return null;
    s=s.trim();
    // YYYY-MM → last day of month
    if(/^\d{4}-\d{2}$/.test(s)){
      const [yr,mo]=s.split("-").map(Number);
      return new Date(yr,mo,0);
    }
    // YYYY-MM-DD (ISO)
    if(/^\d{4}-\d{2}-\d{2}$/.test(s)) return new Date(s);
    // DD/MM/YYYY or MM/DD/YYYY
    if(/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(s)){
      const [a,b,y]=s.split("/").map(Number);
      if(a>12) return new Date(y,b-1,a); // must be DD/MM
      if(b>12) return new Date(y,a-1,b); // must be MM/DD
      return new Date(y,b-1,a);          // default DD/MM
    }
    // DD-MM-YYYY or MM-DD-YYYY
    if(/^\d{1,2}-\d{1,2}-\d{4}$/.test(s)){
      const [a,b,y]=s.split("-").map(Number);
      if(a>12) return new Date(y,b-1,a);
      if(b>12) return new Date(y,a-1,b);
      return new Date(y,b-1,a);
    }
    // "26 September 2026" or "26 Sep 2026" (British)
    const bL=s.match(/^(\d{1,2})\s+([A-Za-z]+)\s+(\d{4})$/);
    if(bL){
      const mi=ML.indexOf(bL[2].toLowerCase())>=0?ML.indexOf(bL[2].toLowerCase()):MS.indexOf(bL[2].toLowerCase().slice(0,3));
      if(mi>=0) return new Date(+bL[3],mi,+bL[1]);
    }
    // "September 26, 2026" or "Sep 26, 2026" (American)
    const aL=s.match(/^([A-Za-z]+)\s+(\d{1,2}),?\s+(\d{4})$/);
    if(aL){
      const mi=ML.indexOf(aL[1].toLowerCase())>=0?ML.indexOf(aL[1].toLowerCase()):MS.indexOf(aL[1].toLowerCase().slice(0,3));
      if(mi>=0) return new Date(+aL[3],mi,+aL[2]);
    }
    // "Jan 2029" or "January 2029" — last day of that month
    const mY=s.match(/^([A-Za-z]+)\s+(\d{4})$/);
    if(mY){
      const mi=ML.indexOf(mY[1].toLowerCase())>=0?ML.indexOf(mY[1].toLowerCase()):MS.indexOf(mY[1].toLowerCase().slice(0,3));
      if(mi>=0) return new Date(+mY[2],mi+1,0); // day 0 of next month = last day of this month
    }
    return null;
  };

  // sync display when value set externally (edit prefill)
  // uses parse() so "Jun 2030" → last day of June, not June 1
  useEffect(()=>{
    if(value){
      if(!/^\d{4}-\d{2}-\d{2}$/.test(value)){
        const dt=parse(value);
        if(dt&&!isNaN(dt)){
          const normalized=toInternal(dt);
          onChange(normalized); setRaw(toDisplay(normalized)); return;
        }
      }
      setRaw(toDisplay(value));
    } else setRaw("");
  },[value]);

  const handleText = (e) => {
    const v=e.target.value;
    setRaw(v);
    if(!v){ setErr(""); onChange(""); return; }
    const dt=parse(v);
    if(dt&&!isNaN(dt)){ setErr(""); onChange(toInternal(dt)); }
    else setErr("Accepts: DD/MM/YYYY · MM/DD/YYYY · YYYY-MM-DD · YYYY-MM · 26 Sep 2026 · September 26, 2026");
  };

  const handlePicker = (e) => {
    const v=e.target.value;
    setRaw(toDisplay(v));
    setErr("");
    onChange(v);
  };

  return(
    <div style={{position:"relative"}}>
      <input value={raw||(value?toDisplay(value):"")}
        onChange={handleText}
        placeholder="DD/MM/YYYY, MM/DD/YYYY, YYYY-MM-DD, YYYY-MM, 26 Sep 2026…"
        style={{width:"100%",boxSizing:"border-box",padding:"7px 36px 7px 10px",
          border:`1px solid ${err?"#EF4444":"#E5E7EB"}`,borderRadius:"8px",
          fontSize:"13px",outline:"none",fontFamily:"inherit"}}/>
      {/* Hidden native date picker triggered by calendar icon */}
      <input ref={pickerRef} type="date" value={/^\d{4}-\d{2}-\d{2}$/.test(value||"")?value:""} onChange={handlePicker}
        style={{position:"absolute",opacity:0,width:"28px",height:"28px",right:"4px",top:"4px",cursor:"pointer",padding:0,border:"none"}}/>
      {/* Calendar icon */}
      <div onClick={()=>pickerRef.current?.showPicker?.()}
        style={{position:"absolute",right:"8px",top:"50%",transform:"translateY(-50%)",
          cursor:"pointer",color:"#9CA3AF",pointerEvents:"none"}}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <rect x="3" y="4" width="18" height="18" rx="2"/>
          <line x1="16" y1="2" x2="16" y2="6"/>
          <line x1="8" y1="2" x2="8" y2="6"/>
          <line x1="3" y1="10" x2="21" y2="10"/>
        </svg>
      </div>
      {err&&<div style={{fontSize:"10px",color:"#EF4444",marginTop:"2px"}}>{err}</div>}
    </div>
  );
}

export default function CaptureScreen({ session, onLogout }) {
  return (
    <AppShell session={session} onLogout={onLogout}
      overrideContent={<CaptureContent session={session}/>}
      hideNav={true}
      hideFilterButtons={true}
      allowNavOverride={true}/>
  );
}